// Form state for the whole-object writes of A3 (`PUT` replaces everything): every field as the
// inputs hold it, and the one conversion to the request body, where empty optional text is null.
import type { z } from "zod";

import type { Brand, Color, Font } from "../../api/generated/model";
import { BrandIn } from "../../api/generated/zod";

export interface ColorDraft {
  hex: string;
  label: string;
}

export interface FontDraft {
  name: string;
  source: Font["source"];
}

export interface BrandDraft {
  name: string;
  description: string;
  website: string;
  logoAssetId: string;
  /** The logo's picture while the form is open: its signed URL, never sent. */
  logoUrl: string | null;
  colors: ColorDraft[];
  fonts: FontDraft[];
  toneOfVoice: string;
  guidelines: string;
  audience: string;
}

/** Empty text means "no value", which the API spells null. */
export function orNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === "" ? null : trimmed;
}

export function colorsIn(colors: ColorDraft[]): Color[] {
  return colors.map((color) => ({ hex: color.hex.trim(), label: orNull(color.label) }));
}

export function colorDrafts(colors: Color[]): ColorDraft[] {
  return colors.map((color) => ({ hex: color.hex, label: color.label ?? "" }));
}

export function brandDraft(brand?: Brand): BrandDraft {
  return {
    name: brand?.name ?? "",
    description: brand?.description ?? "",
    website: brand?.website ?? "",
    logoAssetId: brand?.logo_asset_id ?? "",
    logoUrl: brand?.logo_url ?? null,
    colors: colorDrafts(brand?.colors ?? []),
    fonts: (brand?.fonts ?? []).map((font) => ({ name: font.name, source: font.source })),
    toneOfVoice: brand?.tone_of_voice ?? "",
    guidelines: brand?.guidelines ?? "",
    audience: brand?.audience ?? "",
  };
}

/** The request body, checked by the generated schema. AI output is never part of it. */
export function brandIn(draft: BrandDraft) {
  return BrandIn.safeParse({
    name: draft.name.trim(),
    description: draft.description.trim(),
    website: orNull(draft.website),
    logo_asset_id: orNull(draft.logoAssetId),
    colors: colorsIn(draft.colors),
    fonts: draft.fonts.map((font) => ({ name: font.name.trim(), source: font.source })),
    tone_of_voice: orNull(draft.toneOfVoice),
    guidelines: orNull(draft.guidelines),
    audience: orNull(draft.audience),
  });
}

/** The first problem of each request field, keyed by its name in the body. */
export function fieldIssues(error: z.ZodError): Map<string, z.core.$ZodIssue> {
  const issues = new Map<string, z.core.$ZodIssue>();
  for (const issue of error.issues) {
    const field = String(issue.path[0]);
    if (!issues.has(field)) issues.set(field, issue);
  }
  return issues;
}
